import React, { useState } from 'react';
import {
  Drawer, List, ListItem, ListItemText, IconButton, Switch, ListItemIcon, Divider,
  Box, Typography, useMediaQuery, useTheme, Toolbar, AppBar, Tooltip
} from '@mui/material';
import {
  FaHome, FaUser, FaCog, FaSignOutAlt, FaChevronLeft, FaChevronRight,
  FaBars, FaClipboardList, FaNotesMedical
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import UserMenu from './UserMenu'; // ajusta la ruta si tu estructura es distinta

const Sidebar = ({ toggleTheme, onLogout, darkMode, isAuthenticated = false, isAdminUser = false, }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);

  const drawerWidth = 240;
  const collapsedWidth = 64;
  const currentDrawerWidth = isMobile ? 0 : (desktopOpen ? drawerWidth : collapsedWidth);


  const perfil = (localStorage.getItem('perfil') || '').toLowerCase();
  const isAdmin = ['admin', 'administrador'].includes(perfil);


  const handleDrawerToggle = () => {
    if (isMobile) setMobileOpen(!mobileOpen);
    else setDesktopOpen(!desktopOpen);
  };

  const showText = isMobile ? mobileOpen : desktopOpen; // ← si está cerrado, solo iconos
  const listItemBaseSx = {
    '&:hover': { backgroundColor: 'rgba(0, 180, 216, 0.1)', borderLeft: '3px solid #00b4d8' },
    pl: isMobile ? 2 : desktopOpen ? 3 : 2,
    py: 1.5
  };
  const iconSx = { color: '#0077b6', minWidth: showText ? 40 : 0, display: 'flex', justifyContent: 'center' };

  const MenuItem = ({ to, icon, label, onClick }) => {
    const item = (
      <ListItem
        button
        component={Link}
        to={to}
        onClick={onClick}
        sx={listItemBaseSx}
      >
        <ListItemIcon sx={iconSx}>{icon}</ListItemIcon>
        {showText && (
          <ListItemText
            primary={label}
            primaryTypographyProps={{ color: '#0077b6', fontWeight: '500' }}
          />
        )}
      </ListItem>
    );
    return showText ? item : <Tooltip title={label} placement="right">{item}</Tooltip>;
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: showText ? 'space-between' : 'center',
          borderBottom: '1px solid rgba(0, 180, 216, 0.2)',
          py: 1,
          px: 1.25
        }}
      >
        {showText ? (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              background: 'linear-gradient(to right, #0077b6, #00b4d8)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            BATSA
          </Typography>
        ) : (
          // “logo” mini cuando está colapsado
          <Box
            sx={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'linear-gradient(135deg, #0077b6 0%, #00b4d8 100%)'
            }}
          />
        )}

        {/* Botón colapsar/expandir (solo desktop) */}
        {!isMobile && (
          <IconButton size="small" onClick={handleDrawerToggle} sx={{ color: '#0077b6' }}>
            {desktopOpen ? <FaChevronLeft /> : <FaChevronRight />}
          </IconButton>
        )}
      </Toolbar>

      <List sx={{ overflow: 'auto', flexGrow: 1 }}>
        <MenuItem
          to="/"
          label="Inicio"
          icon={<FaHome size={20} />}
          onClick={isMobile ? handleDrawerToggle : undefined}
        />

        {isAuthenticated && (
          <>
            <MenuItem
              to="/perfil"
              label="Perfil"
              icon={<FaUser size={20} />}
              onClick={isMobile ? handleDrawerToggle : undefined}
            />
            <MenuItem
              to="/historial"
              label="Historial Médico"
              icon={<FaNotesMedical size={20} />}
              onClick={isMobile ? handleDrawerToggle : undefined}
            />


            {isAdminUser && (
              <MenuItem
                to="/registro"
                label="Gestión de Usuarios"
                icon={<FaClipboardList size={20} />}
                onClick={isMobile ? handleDrawerToggle : undefined}
              />

            )}

            <MenuItem
              to="/configuracion"
              label="Configuración"
              icon={<FaCog size={20} />}
              onClick={isMobile ? handleDrawerToggle : undefined}
            />
          </>
        )}

        {/* Switch modo oscuro */}
        <ListItem sx={{ ...listItemBaseSx, '&:hover': { backgroundColor: 'transparent', borderLeft: 'none' } }}>
          <ListItemIcon sx={iconSx}>
            <Switch
              checked={darkMode}
              onChange={toggleTheme}
              color="primary"
              size="medium"
              sx={{
                '& .MuiSwitch-switchBase': { color: '#90e0ef' },
                '& .MuiSwitch-track': { backgroundColor: '#caf0f8' }
              }}
            />
          </ListItemIcon>
          {showText && (
            <ListItemText
              primary="Modo Oscuro"
              primaryTypographyProps={{ color: '#0077b6', fontWeight: '500' }}
            />
          )}
        </ListItem>
      </List>

      {/* Login / Logout */}
      <Box sx={{ mt: 'auto', p: 1 }}>
        <Divider sx={{ borderColor: 'rgba(0, 180, 216, 0.2)', mb: 1 }} />
        {isAuthenticated ? (
          <ListItem
            button
            onClick={() => {
              onLogout();
              if (isMobile) handleDrawerToggle();
            }}
            sx={{
              '&:hover': { backgroundColor: 'rgba(255, 86, 86, 0.1)', borderLeft: '3px solid #ff5656' },
              pl: isMobile ? 2 : desktopOpen ? 3 : 2,
              py: 1.5
            }}
          >
            <ListItemIcon sx={{ color: '#ff5656', minWidth: showText ? 40 : 0, display: 'flex', justifyContent: 'center' }}>
              <FaSignOutAlt size={20} />
            </ListItemIcon>
            {showText && (
              <ListItemText
                primary="Cerrar Sesión"
                primaryTypographyProps={{ color: '#ff5656', fontWeight: '500' }}
              />
            )}
          </ListItem>
        ) : (
          <MenuItem
            to="/login"
            label="Iniciar Sesión"
            icon={<FaSignOutAlt size={20} />}
            onClick={isMobile ? handleDrawerToggle : undefined}
          />
        )}
      </Box>
    </Box>
  );

  return (
    <>
      {/* AppBar en móvil y desktop con menú de usuario */}
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          width: `calc(100% - ${currentDrawerWidth}px)`,
          ml: `${currentDrawerWidth}px`,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
        }}
      >
        <Toolbar sx={{ minHeight: 56 }}>
          {isMobile && (
            <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
              <FaBars />
            </IconButton>
          )}
          <Typography variant="h6" noWrap sx={{ color: '#0077b6', flexGrow: 1 }}>
            BATSA
          </Typography>
          {isAuthenticated && (
            <UserMenu
              onLogout={() => {
                onLogout?.();
                if (isMobile) setMobileOpen(false);
              }}
              displayName={localStorage.getItem('nombreUsuario') || 'Usuario'}
            />
          )}
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box component="nav" sx={{ width: { sm: desktopOpen ? drawerWidth : collapsedWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : desktopOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: isMobile ? drawerWidth : (desktopOpen ? drawerWidth : collapsedWidth),
              background: 'linear-gradient(to bottom, #ffffff 0%, #f5fbff 100%)',
              borderRight: '1px solid rgba(0, 180, 216, 0.2)',
              boxShadow: '2px 0 10px rgba(0, 150, 200, 0.1)',
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen
              })
            }
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>
    </>
  );
};

export default Sidebar;
